import {
  geocodeByAddress,
  geocodeByPlaceId,
  getLatLng,
} from "react-places-autocomplete";
import { getLocationVars } from "~/lib/helpers/googleLocationDataManager";
import { getCountryName } from "~/lib/helpers/constants";
import {
  COUNTRY_PAGE_PREFIX,
  locationNameToSlug,
} from "~/components/blocks/Geosearch/locationGeosearch";

export const RECENT_SEARCHES_STORAGE_KEY = "recentSearchesV2";

export interface SearchItem {
  title: string;
  description: null;
  urlString: string;
  placeId?: string;
  latitude?: number;
  longitude?: number;
  country?: string;
}

export type RecentSearchMetadata = Pick<
  SearchItem,
  "placeId" | "latitude" | "longitude" | "country"
>;

export default async function buildSearchUrl(
  selected: string,
  placeId: string | null,
  firstSuggestion: any,
  setAddress: (address: string) => void,
  setRecentSearches: (searches: SearchItem[]) => void,
  recentSearches: SearchItem[],
): Promise<string> {
  if (!placeId && firstSuggestion && firstSuggestion.placeId) {
    selected = firstSuggestion.description;
    placeId = firstSuggestion.placeId;
  }

  setAddress(selected);

  try {
    let res = placeId
      ? await geocodeByPlaceId(placeId)
      : await geocodeByAddress(selected);

    const {
      state_short,
      state_long,
      city,
      country_short,
      streetName,
      searchOnLatLng,
    } = getLocationVars(res);
    let urlString = "";

    // Convert GB to UK
    const normalizedCountry =
      country_short?.toLowerCase() === "gb"
        ? "uk"
        : country_short?.toLowerCase();

    const urlParts = [];
    if (state_short && state_long) {
      const useStateShort = ["us", "au", "za"].includes(normalizedCountry);
      urlParts.push(
        useStateShort ? state_short.toLowerCase() : state_long.toLowerCase(),
      );
    }

    if (city) {
      urlParts.push(city.toLowerCase().replace(/\s+/g, "-"));
    }

    // Property pages live at /{country}/{state}/{city}
    if (urlParts.length > 0 && normalizedCountry) {
      urlString = `/${normalizedCountry}/${urlParts.join("/")}`;
    } else if (normalizedCountry) {
      urlString = `/${normalizedCountry}/${COUNTRY_PAGE_PREFIX}${locationNameToSlug(getCountryName(normalizedCountry))}`;
    } else {
      urlString = `/us/${COUNTRY_PAGE_PREFIX}${locationNameToSlug(getCountryName("us"))}`;
    }

    // Add coordinates if needed
    const { lat, lng } = await getLatLng(res[0]);

    // Return final URL
    if (streetName || searchOnLatLng) {
      urlString = `${urlString}?latitude=${lat}&longitude=${lng}&zoom=15${streetName ? `&streetName=${streetName}` : ""}`;
    }
    //   // Add the selected search to the beginning
    if (selected) {
      updateRecentSearches(
        selected,
        urlString,
        recentSearches,
        setRecentSearches,
        {
          placeId: placeId || undefined,
          latitude: lat,
          longitude: lng,
          country: normalizedCountry,
        },
      );
    }
    if (urlString[0] === '/') {

      return urlString;
    }
    return `/${urlString}`
  } catch (err) {
    console.error(err);
    return "";
  }
}
export interface Suggestion {
  placeId: string;
  description: string;
}
export async function handleSelectLocation(
  selected: string,
  placeId: string | null,
  firstSuggestion: Suggestion | null,
  setAddress: (address: string) => void,
  setRecentSearches: (searches: SearchItem[]) => void,
  recentSearches: SearchItem[],
) {
  return await buildSearchUrl(
    selected,
    placeId,
    firstSuggestion,
    setAddress,
    setRecentSearches,
    recentSearches,
  );
}
export function handleError(status: any, clearSuggestions: () => void) {
  clearSuggestions();
  return status;
}

export function updateRecentSearches(
  selected: string,
  urlString: string,
  recentSearches: SearchItem[],
  setRecentSearches: (searches: SearchItem[]) => void,
  metadata?: RecentSearchMetadata,
) {
  let updatedSearches = [...recentSearches];
  const existingIndex = updatedSearches.findIndex(
    (item) => item.title === selected,
  );

  if (existingIndex !== -1) {
    updatedSearches.splice(existingIndex, 1);
  }

  const newSearchItem: SearchItem = {
    title: selected,
    description: null,
    urlString: urlString,
    ...metadata,
  };

  // Add new search at the beginning and keep only the most recent 3 searches
  updatedSearches = [newSearchItem, ...updatedSearches].slice(0, 3);
  setRecentSearches(updatedSearches);
  localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(updatedSearches));
}

/**
 * Parse a path produced by {@link buildSearchUrl} / Places autocomplete, e.g.
 * `/uk/england/london?latitude=...` or `/us/texas/austin?...`
 * so area pages can apply `country`, `state`, and `city` query params.
 */
export function parseAutocompleteSearchLocation(location: string): {
  countryCode: string;
  state: string;
  city: string;
  latitude: string;
  longitude: string;
  placeId: string;
  text: string;
} | null {
  if (!location || typeof location !== "string") return null;
  const [pathPart = "", queryPart = ""] = location.split("?");
  const path = pathPart.startsWith("/") ? pathPart.slice(1) : pathPart;
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  let countryCode = "us";
  let state = "";
  let city = "";
  let latitude = "";
  let longitude = "";
  let placeId = "";
  let text = "";

  if (segments[0] === "search") {
    countryCode = "us";
    state = segments[1] || "";
    city = segments[2] || "";
  } else if (segments[1] === "search") {
    countryCode = segments[0] || "us";
    state = segments[2] || "";
    city = segments[3] || "";
  } else {
    return null;
  }

  if (queryPart) {
    const query = new URLSearchParams(queryPart);
    latitude = query.get("latitude") || "";
    longitude = query.get("longitude") || "";
    placeId = query.get("placeId") || "";
    text = query.get("text") || "";
  }

  return { countryCode, state, city, latitude, longitude, placeId, text };
}
